import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Settings, Truck, Phone, Mail, Trash2, X } from 'lucide-react';
import { apiFetch } from '../../../utils/api';
import { useToast } from '../../../components/ui/Toast';
import './inventory-tracker.css';

const formatMoney = (n) => `₦${Number(n || 0).toLocaleString()}`;

/**
 * Modal to create / edit a reorder point for a product. Supports
 * setting min qty, target qty (reorder-up-to level), and an optional
 * supplier. Wired to POST /reorder-points (upsert).
 */
const ReorderPointModal = ({
  open, onClose, onSaved, onDeleted,
  product, existingId, initialValues, suppliers, locations,
}) => {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(initialValues || {});

  useEffect(() => {
    if (open) setValues(initialValues || {});
  }, [open, initialValues]);

  if (!open) return null;

  const handleChange = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const save = async () => {
    const min = Number(values.minQty);
    if (!Number.isFinite(min) || min < 0) {
      toast.error('Minimum quantity is required and must be 0 or greater');
      return;
    }
    // If merchant left "Reorder up to" blank, auto-fill with a sensible
    // default: 3× min (matches industry practice — Shopify / Cin7 default
    // to a multiplier when merchant only sets a minimum). Ensures they
    // can save with the least friction. Falls back to min + 10 if
    // 3 × min would be 0 (edge case of min = 0).
    let target = Number(values.targetQty);
    if (!Number.isFinite(target) || target <= 0) {
      target = Math.max(min * 3, min + 10);
    }
    if (target < min) {
      toast.error('"Reorder up to" must be at least the minimum');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/merchants/inventory/reorder-points', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: product.id,
          locationId: values.locationId || null,
          minQty: min,
          targetQty: target,
          supplierId: values.supplierId || null,
          autoSuggest: true,
        }),
      });
      const payload = await res.json();
      if (!res.ok || !payload.success) {
        throw new Error(payload.error || 'Unable to save rule');
      }
      toast.success('Reorder rule saved');
      onSaved(payload.reorderPoint);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!existingId) return;
    if (!window.confirm('Remove this reorder rule?')) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/merchants/inventory/reorder-points/${existingId}`, { method: 'DELETE' });
      const payload = await res.json();
      if (!res.ok || !payload.success) throw new Error(payload.error || 'Unable to delete');
      toast.success('Reorder rule removed');
      onDeleted(existingId);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="itk-modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="itk-modal">
        <header className="itk-modal-head">
          <h3>Reorder rule — {product.name}</h3>
          <button type="button" className="itk-btn" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>
        <div className="itk-modal-body">
          <label>
            Minimum quantity <span style={{ color: '#dc2626' }}>*</span>
            <input type="number" min="0" value={values.minQty ?? ''} onChange={handleChange('minQty')} placeholder="Reorder when at or below this qty" required />
            <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>Required. When stock hits or drops below this, we'll flag it.</span>
          </label>
          <label>
            Reorder up to <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>(optional)</span>
            <input type="number" min="1" value={values.targetQty ?? ''} onChange={handleChange('targetQty')} placeholder={`Defaults to ${Math.max(Number(values.minQty || 0) * 3, Number(values.minQty || 0) + 10)}`} />
            <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>How much to reorder up to. Leave blank to use 3× your minimum.</span>
          </label>
          {locations?.length > 0 && (
            <label>
              Location (leave blank for merchant-wide rule)
              <select value={values.locationId || ''} onChange={handleChange('locationId')}>
                <option value="">All locations</option>
                {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
              </select>
            </label>
          )}
          <label>
            Suggested supplier (optional)
            <select value={values.supplierId || ''} onChange={handleChange('supplierId')}>
              <option value="">No supplier</option>
              {(suppliers || []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.leadTimeDays ? ` — ${s.leadTimeDays}d lead` : ''}</option>
              ))}
            </select>
          </label>
        </div>
        <footer className="itk-modal-foot">
          {existingId && (
            <button type="button" className="itk-btn danger" onClick={remove} disabled={saving}>
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button type="button" className="itk-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="itk-btn primary" onClick={save} disabled={saving}>
            {saving ? <Loader2 size={14} className="spin" /> : null}
            Save rule
          </button>
        </footer>
      </div>
    </div>
  );
};

/**
 * Reorder Radar tab — surfaces products at/below their reorder point,
 * grouped by suggested supplier. Legacy (Product.lowStockThreshold)
 * triggers land in the UNASSIGNED bucket so nobody's blind spot.
 */
const ReorderRadarTab = () => {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [modal, setModal] = useState(null);
  const [reorderList, setReorderList] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [radarRes, rpRes] = await Promise.all([
        apiFetch('/merchants/inventory/reorder-radar'),
        apiFetch('/merchants/inventory/reorder-points'),
      ]);
      const radarJson = await radarRes.json();
      const rpJson = await rpRes.json();
      if (!radarRes.ok || !radarJson.success) throw new Error(radarJson.error || 'Unable to load radar');
      if (!rpRes.ok || !rpJson.success) throw new Error(rpJson.error || 'Unable to load rules');
      setData(radarJson);
      setReorderList(rpJson.reorderPoints || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Pull the merchant's supplier list once so the modal has a dropdown.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/merchants/suppliers');
        if (!res.ok) return;
        const payload = await res.json();
        setSuppliers(payload.suppliers || payload.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const openModal = (item) => {
    const existing = reorderList.find(
      (r) => r.productId === item.productId && (r.locationId || null) === (item.locationId || null)
    );
    setModal({
      product: { id: item.productId, name: item.productName },
      existingId: existing?.id || null,
      initialValues: {
        minQty: existing?.minQty ?? item.minQty ?? '',
        targetQty: existing?.targetQty ?? item.targetQty ?? '',
        locationId: item.locationId || existing?.locationId || '',
        supplierId: existing?.supplierId || '',
      },
    });
  };

  const composeWhatsappUrl = (group) => {
    const supplier = group.supplier;
    if (!supplier?.whatsappNumber && !supplier?.phone) return null;
    const number = String(supplier.whatsappNumber || supplier.phone).replace(/\D/g, '');
    const lines = group.items.map((it) => `• ${it.productName} — ${it.suggestedReorderQty ?? '?'} units`);
    const msg = `Hi ${supplier.name}, we'd like to place a restock order:\n\n${lines.join('\n')}\n\nSent via Revstar.`;
    return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
  };

  const composeEmailUrl = (group) => {
    const supplier = group.supplier;
    if (!supplier?.email) return null;
    const lines = group.items.map((it) => `- ${it.productName}: ${it.suggestedReorderQty ?? '?'} units`);
    const subject = 'Restock request';
    const body = `Hi ${supplier.name},\n\nWe'd like to place a restock order:\n\n${lines.join('\n')}\n\nThanks.`;
    return `mailto:${supplier.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="itk-panel">
      <div className="itk-panel-header">
        <div>
          <h2>Reorder radar</h2>
          <p>Products at or below their reorder point, grouped by supplier lead time.</p>
        </div>
        <button type="button" className="itk-btn" onClick={load} disabled={loading}>
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="itk-loading"><Loader2 size={18} className="spin" /> Loading radar…</div>
      ) : !data || data.totals.groups === 0 ? (
        <div className="itk-empty">
          <strong>Nothing to reorder right now</strong>
          Configure reorder points on your key products to see them here when stock gets low.
        </div>
      ) : (
        <>
          <div className="itk-metrics">
            <div className="itk-card">
              <p className="itk-card-title">Rules triggered</p>
              <div className="itk-card-value">{data.totals.rulesTriggered}</div>
              <div className="itk-card-caption">Products at or below their configured minimum</div>
            </div>
            <div className="itk-card">
              <p className="itk-card-title">Legacy threshold flags</p>
              <div className="itk-card-value">{data.totals.legacyTriggered}</div>
              <div className="itk-card-caption">Products without a rule but hitting the old low-stock line</div>
            </div>
            <div className="itk-card">
              <p className="itk-card-title">Supplier groups</p>
              <div className="itk-card-value">{data.totals.groups}</div>
              <div className="itk-card-caption">Where to send restock POs</div>
            </div>
          </div>

          {data.groups.map((group, i) => {
            const waUrl = composeWhatsappUrl(group);
            const emailUrl = composeEmailUrl(group);
            return (
              <section key={group.supplierId || `unassigned-${i}`} className="itk-group">
                <div className="itk-group-header">
                  <div>
                    <h3>
                      {group.supplier ? (
                        <>
                          <Truck size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                          {group.supplier.name}
                        </>
                      ) : (
                        'Unassigned (no supplier)'
                      )}
                    </h3>
                    <p>
                      {group.itemCount} product{group.itemCount === 1 ? '' : 's'} · {group.totalSuggestedUnits} suggested units
                      {group.supplier?.leadTimeDays ? ` · ${group.supplier.leadTimeDays}d lead time` : ''}
                    </p>
                    <div className="itk-supplier-chips">
                      {waUrl && <a className="itk-chip" href={waUrl} target="_blank" rel="noreferrer"><Phone size={12} /> WhatsApp</a>}
                      {emailUrl && <a className="itk-chip" href={emailUrl}><Mail size={12} /> Email</a>}
                    </div>
                  </div>
                  <div className="itk-group-summary">
                    <span>Total units</span>
                    <strong>{group.totalSuggestedUnits}</strong>
                  </div>
                </div>

                {group.items.map((it) => (
                  <div key={`${it.reorderPointId || it.productId}-${it.locationId || 'none'}`} className="itk-radar-item">
                    <div className="name">
                      <strong>{it.productName}</strong>
                      <span>
                        {it.sku ? `SKU ${it.sku} · ` : ''}
                        {it.source === 'LEGACY_THRESHOLD' ? 'From low-stock threshold' : 'From reorder rule'}
                        {it.locationName ? ` · ${it.locationName}` : ''}
                      </span>
                    </div>
                    <div className="stat">
                      <span>On hand</span>
                      <strong>{it.onHand}</strong>
                    </div>
                    <div className="stat warn">
                      <span>Min</span>
                      <strong>{it.minQty}</strong>
                    </div>
                    <div className="stat">
                      <span>Suggest</span>
                      <strong>{it.suggestedReorderQty ?? '—'}</strong>
                    </div>
                    <div className="stat">
                      <span>Days left</span>
                      <strong>{it.daysOfStock ?? '—'}</strong>
                    </div>
                    <button type="button" className="itk-btn" onClick={() => openModal(it)} title="Edit rule">
                      <Settings size={14} />
                    </button>
                  </div>
                ))}
              </section>
            );
          })}
        </>
      )}

      {modal && (
        <ReorderPointModal
          open
          product={modal.product}
          existingId={modal.existingId}
          initialValues={modal.initialValues}
          suppliers={suppliers}
          locations={[]}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
          onDeleted={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
};

export default ReorderRadarTab;
